import { Main, Models, DataTable, Pagination, StatCard, StatusBadge, Assets } from 'utils/imports.utils';
import { IDataTableColumn } from 'common_components/ui/data_table/data_table.ui';
import AvatarInitials from 'common_components/ui/avatar_initials/avatar_initials.ui';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSetState, buildAdminListQuery, formatViewCount, getAdminUserLabel } from 'utils/functions.utils';
import { AdminUserDetail, AdminProjectRow } from 'interfaces/admin.interface';
import AdminActions from 'common_components/ui/admin_actions/admin_actions.ui';
import { STRINGS } from 'constants/strings.constant';
import AdminPage from 'common_components/ui/admin_page/admin_page.ui';
import './admin_user_detail.screen.scss';

const PAGE_SIZE = 20;

const AdminUserDetailScreen = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const hasLoadedOnce = useRef(false);
  const [state, setState] = useSetState({
    detail: null as AdminUserDetail | null,
    projects: [] as AdminProjectRow[],
    totalDocs: 0,
    page: 1,
    loading: true,
  });

  const fetchDetail = async () => {
    if (!userId) return;
    try {
      const response: any = await Models.auth.adminGetUserDetail(userId);
      setState({ detail: response?.data || null });
    } catch (err) {
      console.error('Failed to fetch user detail', err);
    }
  };

  const fetchProjects = async (page: number) => {
    if (!userId) return;
    if (!hasLoadedOnce.current) {
      setState({ loading: true });
    }
    try {
      const query = buildAdminListQuery({ skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE });
      const response: any = await Models.auth.adminGetUserProjects(userId, query);
      setState({
        projects: response?.data?.docs || [],
        totalDocs: response?.data?.totalDocs || 0,
        loading: false,
      });
    } catch (err) {
      console.error('Failed to fetch user projects', err);
      setState({ loading: false });
    } finally {
      hasLoadedOnce.current = true;
    }
  };

  useEffect(() => {
    let token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchDetail();
  }, [userId]);

  useEffect(() => {
    let token = localStorage.getItem('token');
    if (token) {
      fetchProjects(state.page);
    }
  }, [userId, state.page]);

  const columns: IDataTableColumn[] = [
    { key: 'project_name', label: 'Project' },
    {
      key: 'project_status',
      label: 'Status',
      render: (row: AdminProjectRow) =>
        row.project_status ? (
          <StatusBadge status={row.project_status} />
        ) : (
          '—'
        ),
    },
    {
      key: 'total_views',
      label: 'Total views',
      align: 'right',
      render: (row: AdminProjectRow) => formatViewCount(row.total_views),
    },
    {
      key: 'created_at',
      label: 'Created',
      align: 'right',
      render: (row: AdminProjectRow) =>
        row.created_at ? new Date(row.created_at).toLocaleDateString() : '—',
    },
  ];

  return (
    <Main backgroundColor="#F7F3E9" isRenderOnlyLogo={false} isAdmin>
      <AdminPage breadcrumb="Admin / Users / Detail">
      <div className="admin_user_detail">
        <img
          src={Assets.arrow}
          alt="Back"
          className="admin_user_detail_back"
          onClick={() => navigate('/admin/users')}
        />
        {state.detail && (
          <>
            <div className="admin_user_detail_header">
              <AvatarInitials
                size={56}
                name={getAdminUserLabel(state.detail.user)}
              />
              <div className="admin_user_detail_header_text">
                <h2>{state.detail.user.username || 'User'}</h2>
                <p className="admin_user_detail_email">
                  {state.detail.user.email || '—'}
                </p>
              </div>
              {state.detail.user.is_blocked && <StatusBadge status="blocked" label={STRINGS.adminActions.blocked} tone="negative" />}
              <AdminActions
                kind="user"
                isBlocked={Boolean(state.detail.user.is_blocked)}
                onBlockChange={async (blocked: boolean) => {
                  await Models.auth.adminBlockUser(userId as string, { blocked });
                  setState({ detail: { ...state.detail!, user: { ...state.detail!.user, is_blocked: blocked } } });
                }}
                onDelete={async () => {
                  await Models.auth.adminDeleteUser(userId as string);
                  navigate('/admin/users');
                }}
              />
            </div>
            <div className="admin_user_detail_stats">
              <StatCard label="Projects" value={state.detail.stats.project_count} />
              <StatCard label="Targets" value={state.detail.stats.target_count} />
              <StatCard label="Total views" value={formatViewCount(state.detail.stats.total_views)} />
              <StatCard label="Open tickets" value={state.detail.stats.open_ticket_count} />
            </div>
          </>
        )}
        <h3 className="admin_user_detail_projects_title">Projects</h3>
        <div className="admin_user_detail_card"><DataTable
          columns={columns}
          rows={state.projects}
          rowKey="_id"
          loading={state.loading}
          emptyMessage="This user has no projects"
        /></div>
        <Pagination
          currentPage={state.page}
          totalPages={Math.ceil(state.totalDocs / PAGE_SIZE)}
            pageSize={PAGE_SIZE}
            totalItems={state.totalDocs}
          onPageChange={(page) => setState({ page })}
        />
      </div>
      </AdminPage>
    </Main>
  );
};

export default AdminUserDetailScreen;
